import React, { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tag } from "antd";
import { toast } from "react-toastify";
import {
  GroupDto,
  RoleDto,
  UserDto,
  listGroups,
  listRoles,
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  resetPassword,
  revokeUserSessions,
} from "../../../services/adminService";

export default function UsersAdmin() {
  const [users, setUsers] = useState<UserDto[]>([]);
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [groups, setGroups] = useState<GroupDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [adding, setAdding] = useState(false);
  const [pwUser, setPwUser] = useState<UserDto | null>(null);
  const [form] = Form.useForm();
  const [pwForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [u, r, g] = await Promise.all([listUsers(), listRoles(), listGroups()]);
      setUsers(u);
      setRoles(r);
      setGroups(g);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSubmit = async (values: any) => {
    try {
      if (editing) {
        await updateUser(editing.id, {
          email: values.email,
          full_name: values.full_name,
          enabled: values.enabled,
          role_ids: values.role_ids ?? [],
          group_ids: values.group_ids ?? [],
        });
        toast.success("User updated");
      } else {
        await createUser({
          username: values.username,
          password: values.password,
          email: values.email,
          full_name: values.full_name,
          enabled: values.enabled ?? true,
          role_ids: values.role_ids ?? [],
          group_ids: values.group_ids ?? [],
          // Optional: preserve the Keycloak `sub` so settings/audit history matches.
          // Blank → backend auto-generates a fresh UUID.
          id: values.id?.trim() || undefined,
        });
        toast.success("User created");
      }
      setEditing(null);
      setAdding(false);
      form.resetFields();
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onResetPassword = async (values: { password: string }) => {
    if (!pwUser) return;
    try {
      await resetPassword(pwUser.id, values.password);
      toast.success("Password reset (user's sessions revoked)");
      setPwUser(null);
      pwForm.resetFields();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const onDelete = async (id: string) => {
    try {
      await deleteUser(id);
      toast.success("User deleted");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onRevokeSessions = async (id: string) => {
    try {
      await revokeUserSessions(id);
      toast.success("All sessions revoked for user");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const roleOptions = roles.map((r) => ({ label: r.name, value: r.id }));
  const groupOptions = groups.map((g) => ({ label: g.name, value: g.id }));

  const columns = [
    { title: "Username", dataIndex: "username", key: "username" },
    { title: "Name", dataIndex: "full_name", key: "full_name" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Enabled",
      key: "enabled",
      render: (_: any, u: UserDto) => (u.enabled ? <Tag color="green">yes</Tag> : <Tag color="red">no</Tag>),
    },
    {
      title: "Effective roles",
      key: "effective_roles",
      render: (_: any, u: UserDto) => (
        <>
          {u.effective_roles.map((r) => (
            <Tag key={r}>{r}</Tag>
          ))}
        </>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, u: UserDto) => (
        <Space wrap>
          <Button
            size="small"
            onClick={() => {
              setEditing(u);
              form.setFieldsValue({
                username: u.username,
                email: u.email,
                full_name: u.full_name,
                enabled: u.enabled,
                role_ids: u.roles.map((r) => r.id),
                group_ids: u.groups.map((g) => g.id),
              });
            }}
          >
            Edit
          </Button>
          <Button size="small" onClick={() => setPwUser(u)}>
            Reset PW
          </Button>
          <Popconfirm title="Revoke all sessions for this user?" onConfirm={() => onRevokeSessions(u.id)}>
            <Button size="small">Revoke sessions</Button>
          </Popconfirm>
          <Popconfirm title="Delete this user?" onConfirm={() => onDelete(u.id)}>
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          onClick={() => {
            setAdding(true);
            form.resetFields();
            form.setFieldsValue({ enabled: true });
          }}
        >
          Add User
        </Button>
      </div>
      <Table rowKey="id" loading={loading} dataSource={users} columns={columns} pagination={false} />

      <Modal
        title={editing ? `Edit User: ${editing.username}` : "Add User"}
        open={adding || editing !== null}
        onCancel={() => {
          setEditing(null);
          setAdding(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: !editing, message: "Username is required" }]}
          >
            <Input disabled={!!editing} />
          </Form.Item>
          {!editing && (
            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, min: 8, message: "Min 8 characters" }]}
            >
              <Input.Password />
            </Form.Item>
          )}
          {!editing && (
            <Form.Item
              label="User ID (UUID) — optional"
              name="id"
              tooltip="Set to the user's existing Keycloak `sub` UUID when migrating, so their settings (user id) and audit history keep matching. Leave blank to auto-generate a new id."
              rules={[
                {
                  validator: (_, value) =>
                    !value ||
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
                      value.trim()
                    )
                      ? Promise.resolve()
                      : Promise.reject(new Error("Must be a valid UUID (or leave blank)")),
                },
              ]}
            >
              <Input placeholder="e.g. 3f2504e0-4f89-41d3-9a0c-0305e82c3301 (Keycloak sub)" />
            </Form.Item>
          )}
          <Form.Item label="Full name" name="full_name">
            <Input />
          </Form.Item>
          <Form.Item label="Email" name="email">
            <Input type="email" />
          </Form.Item>
          <Form.Item label="Enabled" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Direct roles" name="role_ids">
            <Select mode="multiple" options={roleOptions} optionFilterProp="label" allowClear />
          </Form.Item>
          <Form.Item label="Groups" name="group_ids">
            <Select mode="multiple" options={groupOptions} optionFilterProp="label" allowClear />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={pwUser ? `Reset password: ${pwUser.username}` : ""}
        open={pwUser !== null}
        onCancel={() => {
          setPwUser(null);
          pwForm.resetFields();
        }}
        onOk={() => pwForm.submit()}
        destroyOnClose
      >
        <Form form={pwForm} layout="vertical" onFinish={onResetPassword}>
          <Form.Item
            label="New password"
            name="password"
            rules={[{ required: true, min: 8, message: "Min 8 characters" }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
